import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Truck, Package, IndianRupee, MapPin, Settings, 
  TrendingUp, Clock, CheckCircle2, Loader2, RefreshCw,
  Banknote, CreditCard, ArrowDownToLine, ArrowUpFromLine,
  Download, FileSpreadsheet, Calendar, Plus, Calculator,
  AlertTriangle, CheckCircle, XCircle, Trash2
} from 'lucide-react';
import { formatDateIST, formatShortDateIST, formatDateTimeIST } from '../../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CSV Export helper
const exportToCSV = (orders, filename) => {
  const headers = [
    'Order ID',
    'Order Date',
    'Customer Name',
    'Phone',
    'City',
    'State',
    'Pincode',
    'Zone',
    'Payment Method',
    'Payment Status',
    'Order Status',
    'Order Amount',
    'Shipping Cost',
    'COD Fee',
    'Total Logistics Cost',
    'Net Amount',
    'Tracking Number',
    'Courier Name',
    'Items'
  ];
  
  const rows = orders.map(order => [
    order.order_id || order.short_id,
    order.date ? new Date(order.date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '',
    order.customer || '',
    order.phone || '',
    order.city || '',
    order.state || '',
    order.pincode || '',
    order.zone?.toUpperCase() || '',
    order.payment_method === 'cod' ? 'COD' : 'Prepaid',
    order.payment_status || '',
    order.order_status || '',
    order.order_amount || 0,
    order.shipping_cost || 0,
    order.cod_charge || 0,
    order.total_logistics_cost || 0,
    (order.order_amount || 0) - (order.total_logistics_cost || 0),
    order.tracking_number || '',
    order.courier_name || '',
    order.items?.map(i => `${i.title || i.product_id} x${i.quantity}`).join('; ') || ''
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

function AdminLogisticsPage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [rates, setRates] = useState({
    delhi: { base_500g: 50, per_kg: 30, cod_charge: 30 },
    ncr: { base_500g: 70, per_kg: 30, cod_charge: 30 },
    north: { base_500g: 90, per_kg: 30, cod_charge: 30 },
    roi: { base_500g: 110, per_kg: 30, cod_charge: 30 },
  });
  const [savingRates, setSavingRates] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterZone, setFilterZone] = useState('all');
  
  // COD Remittance states
  const [remittances, setRemittances] = useState([]);
  const [remittanceSummary, setRemittanceSummary] = useState(null);
  const [showRemittanceModal, setShowRemittanceModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [selectedRemittance, setSelectedRemittance] = useState(null);
  const [newRemittance, setNewRemittance] = useState({
    period_start: '',
    period_end: '',
    notes: '',
  });
  const [calculatedRemittance, setCalculatedRemittance] = useState(null);
  const [receivedAmount, setReceivedAmount] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [creatingRemittance, setCreatingRemittance] = useState(false);

  const token = localStorage.getItem('admin_token');

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/logistics/dashboard?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDashboard(data);
      setOrders(data.orders || []);
      if (data.rates) {
        setRates(data.rates);
      }
    } catch (error) {
      toast.error('Failed to fetch logistics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRemittances = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/logistics/cod-remittance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRemittances(data.remittances || []);
      setRemittanceSummary(data.summary || null);
    } catch (error) {
      toast.error('Failed to fetch remittances');
    }
  };

  const calculateRemittance = async () => {
    if (!newRemittance.period_start || !newRemittance.period_end) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/logistics/cod-remittance/calculate?start_date=${newRemittance.period_start}&end_date=${newRemittance.period_end}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setCalculatedRemittance(data);
    } catch (error) {
      toast.error('Failed to calculate remittance');
    }
  };

  const createRemittance = async () => {
    setCreatingRemittance(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/logistics/cod-remittance`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(newRemittance),
      });
      
      if (response.ok) {
        toast.success('Remittance period created!');
        setShowRemittanceModal(false);
        setNewRemittance({ period_start: '', period_end: '', notes: '' });
        setCalculatedRemittance(null);
        fetchRemittances();
      } else {
        toast.error('Failed to create remittance period');
      }
    } catch (error) {
      toast.error('Error creating remittance');
    } finally {
      setCreatingRemittance(false);
    }
  };

  const recordPayment = async () => {
    if (!selectedRemittance || !receivedAmount || !receivedDate) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/logistics/cod-remittance/${selectedRemittance.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          received_amount: parseFloat(receivedAmount),
          received_date: receivedDate,
          notes: selectedRemittance.notes,
        }),
      });
      
      const result = await response.json();
      if (response.ok) {
        if (result.status === 'short') {
          toast.warning(`Payment recorded! Courier paid ₹${Math.abs(result.difference)} less than expected`);
        } else if (result.status === 'excess') {
          toast.info(`Payment recorded! Courier paid ₹${result.difference} more than expected`);
        } else {
          toast.success('Payment recorded successfully!');
        }
        setShowRecordPaymentModal(false);
        setSelectedRemittance(null);
        setReceivedAmount('');
        setReceivedDate('');
        fetchRemittances();
      } else {
        toast.error('Failed to record payment');
      }
    } catch (error) {
      toast.error('Error recording payment');
    }
  };

  const deleteRemittance = async (id) => {
    if (!confirm('Are you sure you want to delete this remittance period?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/logistics/cod-remittance/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success('Remittance deleted');
        fetchRemittances();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchRemittances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/logistics/orders?status=${filterStatus}&zone=${filterZone}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
  };

  const saveRates = async () => {
    setSavingRates(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/logistics/rates`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(rates),
      });
      
      if (response.ok) {
        toast.success('Shipping rates updated!');
        setShowRatesModal(false);
        fetchDashboard();
      } else {
        toast.error('Failed to update rates');
      }
    } catch (error) {
      toast.error('Error saving rates');
    } finally {
      setSavingRates(false);
    }
  };

  const updateRate = (zone, field, value) => {
    setRates(prev => ({
      ...prev,
      [zone]: {
        ...prev[zone],
        [field]: parseFloat(value) || 0,
      }
    }));
  };

  const getZoneBadgeColor = (zone) => {
    const colors = {
      delhi: 'bg-blue-100 text-blue-800',
      ncr: 'bg-purple-100 text-purple-800',
      north: 'bg-green-100 text-green-800',
      roi: 'bg-orange-100 text-orange-800',
    };
    return colors[zone] || 'bg-gray-100 text-gray-800';
  };

  const getZoneLabel = (zone) => {
    const labels = {
      delhi: 'Delhi',
      ncr: 'NCR',
      north: 'North',
      roi: 'Rest of India',
    };
    return labels[zone] || zone;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Truck className="w-8 h-8" />
              Logistics Dashboard
            </h1>
            <p className="text-muted-foreground">Track shipments, COD collection & shipping costs</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowRatesModal(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Shipping Rates
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Shipped */}
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Shipped</p>
                <p className="text-3xl font-bold mt-1">{dashboard?.summary?.total_orders || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboard?.summary?.delivered || 0} delivered
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          {/* COD Pending */}
          <Card className="p-5 border-amber-200 bg-amber-50/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-700">COD Pending Collection</p>
                <p className="text-3xl font-bold mt-1 text-amber-800">
                  ₹{(dashboard?.cod_tracking?.pending_amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {dashboard?.cod_tracking?.pending_collection || 0} orders in transit
                </p>
              </div>
              <div className="p-3 bg-amber-200 rounded-lg">
                <Clock className="w-6 h-6 text-amber-700" />
              </div>
            </div>
          </Card>

          {/* COD Collected */}
          <Card className="p-5 border-green-200 bg-green-50/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-green-700">COD Collected</p>
                <p className="text-3xl font-bold mt-1 text-green-800">
                  ₹{(dashboard?.cod_tracking?.collected_amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {dashboard?.cod_tracking?.collected || 0} orders delivered
                </p>
              </div>
              <div className="p-3 bg-green-200 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </Card>

          {/* Logistics Cost */}
          <Card className="p-5 border-red-200 bg-red-50/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-red-700">Total Logistics Cost</p>
                <p className="text-3xl font-bold mt-1 text-red-800">
                  ₹{(dashboard?.costs?.total_logistics_cost || 0).toLocaleString()}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Shipping: ₹{dashboard?.costs?.total_shipping_cost || 0} + COD: ₹{dashboard?.costs?.total_cod_charges || 0}
                </p>
              </div>
              <div className="p-3 bg-red-200 rounded-lg">
                <ArrowUpFromLine className="w-6 h-6 text-red-700" />
              </div>
            </div>
          </Card>
        </div>

        {/* Net Calculation */}
        <Card className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-indigo-100 text-sm">Total Order Value</p>
              <p className="text-2xl font-bold">₹{(dashboard?.costs?.total_order_value || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-indigo-100 text-sm">(-) Logistics Cost</p>
              <p className="text-2xl font-bold">₹{(dashboard?.costs?.total_logistics_cost || 0).toLocaleString()}</p>
            </div>
            <div className="hidden md:flex items-center justify-center">
              <div className="text-4xl">=</div>
            </div>
            <div className="bg-white/20 rounded-lg p-4">
              <p className="text-indigo-100 text-sm">Net After Logistics</p>
              <p className="text-3xl font-bold">₹{(dashboard?.costs?.net_after_logistics || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>

        {/* Zone Breakdown */}
        <Card className="p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Orders by Zone
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(dashboard?.zone_breakdown || {}).map(([zone, count]) => (
              <div key={zone} className="text-center p-4 rounded-lg bg-muted/50">
                <Badge className={getZoneBadgeColor(zone)}>{getZoneLabel(zone)}</Badge>
                <p className="text-2xl font-bold mt-2">{count}</p>
                <p className="text-xs text-muted-foreground">orders</p>
              </div>
            ))}
          </div>
        </Card>

        {/* COD Remittance Tracker */}
        <Card className="p-5 border-amber-200 bg-amber-50/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Banknote className="w-5 h-5 text-amber-700" />
                COD Remittance Tracker
              </h3>
              <p className="text-sm text-muted-foreground">
                Track courier payments for delivered COD orders (every 15 days)
              </p>
            </div>
            <Button onClick={() => setShowRemittanceModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Period
            </Button>
          </div>

          {/* Remittance Summary */}
          {remittanceSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Total Expected</p>
                <p className="text-xl font-bold">₹{remittanceSummary.total_expected?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Total Received</p>
                <p className="text-xl font-bold text-green-600">₹{remittanceSummary.total_received?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Pending Amount</p>
                <p className="text-xl font-bold text-amber-600">₹{remittanceSummary.pending_amount?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Pending Periods</p>
                <p className="text-xl font-bold">{remittanceSummary.pending_periods}</p>
              </div>
            </div>
          )}

          {/* Remittance Periods List */}
          {remittances.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-white">
                    <th className="text-left p-3">Period</th>
                    <th className="text-center p-3">Orders</th>
                    <th className="text-right p-3">COD Amount</th>
                    <th className="text-right p-3">Expected</th>
                    <th className="text-right p-3">Received</th>
                    <th className="text-right p-3">Pay Courier</th>
                    <th className="text-right p-3">Net Profit</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-center p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {remittances.map((rem, idx) => (
                    <tr key={idx} className="border-b hover:bg-white/50">
                      <td className="p-3">
                        <div className="font-medium">
                          {formatShortDateIST(rem.period_start)} - {formatDateIST(rem.period_end)}
                        </div>
                        {rem.notes && <div className="text-xs text-muted-foreground">{rem.notes}</div>}
                      </td>
                      <td className="p-3 text-center">{rem.order_count}</td>
                      <td className="p-3 text-right">₹{rem.total_cod_collected?.toLocaleString()}</td>
                      <td className="p-3 text-right font-medium text-green-600">₹{rem.expected_amount?.toLocaleString()}</td>
                      <td className="p-3 text-right">
                        {rem.received_amount ? (
                          <span className="font-medium text-green-600">₹{rem.received_amount.toLocaleString()}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right text-amber-600">
                        ₹{(rem.courier_charges_to_pay || ((rem.shipping_cost || 0) + (rem.cod_charges || 0))).toLocaleString()}
                      </td>
                      <td className="p-3 text-right font-medium text-blue-600">
                        ₹{(rem.net_after_courier || (rem.total_cod_collected - (rem.shipping_cost || 0) - (rem.cod_charges || 0))).toLocaleString()}
                      </td>
                      <td className="p-3 text-center">
                        {rem.status === 'pending' && (
                          <Badge className="bg-amber-100 text-amber-800">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                        {rem.status === 'received' && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Received
                          </Badge>
                        )}
                        {rem.status === 'short' && (
                          <Badge className="bg-red-100 text-red-800">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Short ₹{Math.abs(rem.difference || 0)}
                          </Badge>
                        )}
                        {rem.status === 'excess' && (
                          <Badge className="bg-blue-100 text-blue-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Excess ₹{rem.difference || 0}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {rem.status === 'pending' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedRemittance(rem);
                                setShowRecordPaymentModal(true);
                              }}
                            >
                              Record Payment
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteRemittance(rem.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-lg">
              <Banknote className="w-12 h-12 mx-auto text-amber-300 mb-3" />
              <p className="text-muted-foreground">No remittance periods created yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "New Period" to track your first courier payment
              </p>
            </div>
          )}
        </Card>

        {/* Quick Export Section */}
        <Card className="p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Courier Reconciliation Reports
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Download CSV reports to reconcile with your courier partner. The CSV includes order details, tracking numbers, shipping costs, and COD amounts.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await fetch(`${API_URL}/api/admin/logistics/orders?status=shipped`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const data = await response.json();
                  const filename = `shipped_orders_${new Date().toISOString().split('T')[0]}.csv`;
                  exportToCSV(data.orders || [], filename);
                  toast.success(`Exported ${data.orders?.length || 0} shipped orders`);
                } catch (e) {
                  toast.error('Export failed');
                }
              }}
            >
              <Truck className="w-4 h-4 mr-2" />
              Shipped Orders
            </Button>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await fetch(`${API_URL}/api/admin/logistics/orders?status=delivered`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const data = await response.json();
                  const filename = `delivered_orders_${new Date().toISOString().split('T')[0]}.csv`;
                  exportToCSV(data.orders || [], filename);
                  toast.success(`Exported ${data.orders?.length || 0} delivered orders`);
                } catch (e) {
                  toast.error('Export failed');
                }
              }}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Delivered Orders
            </Button>
            <Button 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                try {
                  const response = await fetch(`${API_URL}/api/admin/logistics/orders?status=all`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const data = await response.json();
                  const codOrders = (data.orders || []).filter(o => o.payment_method === 'cod');
                  const filename = `cod_orders_${new Date().toISOString().split('T')[0]}.csv`;
                  exportToCSV(codOrders, filename);
                  toast.success(`Exported ${codOrders.length} COD orders`);
                } catch (e) {
                  toast.error('Export failed');
                }
              }}
            >
              <Banknote className="w-4 h-4 mr-2" />
              All COD Orders
            </Button>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await fetch(`${API_URL}/api/admin/logistics/orders?status=all`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const data = await response.json();
                  const filename = `all_orders_${new Date().toISOString().split('T')[0]}.csv`;
                  exportToCSV(data.orders || [], filename);
                  toast.success(`Exported ${data.orders?.length || 0} orders`);
                } catch (e) {
                  toast.error('Export failed');
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              All Orders
            </Button>
          </div>
        </Card>

        {/* Orders Table */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" />
              Shipment Records
            </h3>
            <div className="flex gap-2 flex-wrap">
              <select 
                className="border rounded-md px-3 py-1 text-sm"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setTimeout(fetchOrders, 100);
                }}
              >
                <option value="all">All Status</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
              </select>
              <select 
                className="border rounded-md px-3 py-1 text-sm"
                value={filterZone}
                onChange={(e) => {
                  setFilterZone(e.target.value);
                  setTimeout(fetchOrders, 100);
                }}
              >
                <option value="all">All Zones</option>
                <option value="delhi">Delhi</option>
                <option value="ncr">NCR</option>
                <option value="north">North</option>
                <option value="roi">Rest of India</option>
              </select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const filename = `courier_reconciliation_${new Date().toISOString().split('T')[0]}.csv`;
                  exportToCSV(orders, filename);
                  toast.success('CSV exported successfully!');
                }}
                disabled={orders.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Summary for filtered view */}
          {orders.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 p-3 bg-muted/50 rounded-lg text-sm">
              <div>
                <span className="text-muted-foreground">Orders:</span>
                <span className="font-semibold ml-1">{orders.length}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Order Value:</span>
                <span className="font-semibold ml-1">₹{orders.reduce((sum, o) => sum + (o.order_amount || 0), 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Shipping:</span>
                <span className="font-semibold ml-1 text-red-600">₹{orders.reduce((sum, o) => sum + (o.shipping_cost || 0), 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">COD Fees:</span>
                <span className="font-semibold ml-1 text-red-600">₹{orders.reduce((sum, o) => sum + (o.cod_charge || 0), 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Net:</span>
                <span className="font-semibold ml-1 text-green-600">
                  ₹{orders.reduce((sum, o) => sum + ((o.order_amount || 0) - (o.total_logistics_cost || 0)), 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3">Order ID</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Location</th>
                  <th className="text-left p-3">Zone</th>
                  <th className="text-left p-3">Payment</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Tracking</th>
                  <th className="text-right p-3">Order Amt</th>
                  <th className="text-right p-3">Shipping</th>
                  <th className="text-right p-3">COD Fee</th>
                  <th className="text-right p-3">Net Amt</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/30">
                    <td className="p-3 font-mono text-xs">{order.short_id || order.order_id?.slice(0, 8).toUpperCase()}</td>
                    <td className="p-3 text-xs">{order.date ? new Date(order.date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' }) : '-'}</td>
                    <td className="p-3">
                      <div>{order.customer}</div>
                      <div className="text-xs text-muted-foreground">{order.phone}</div>
                    </td>
                    <td className="p-3 text-xs">
                      {order.city}, {order.state}
                      <div className="text-muted-foreground">{order.pincode}</div>
                    </td>
                    <td className="p-3">
                      <Badge className={getZoneBadgeColor(order.zone)}>
                        {getZoneLabel(order.zone)}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {order.payment_method === 'cod' ? (
                        <Badge className="bg-amber-100 text-amber-800">
                          <Banknote className="w-3 h-3 mr-1" />
                          COD
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          <CreditCard className="w-3 h-3 mr-1" />
                          Prepaid
                        </Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant={order.order_status === 'delivered' ? 'default' : 'secondary'}>
                        {order.order_status}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {order.tracking_number ? (
                        <div className="text-xs">
                          <div className="font-mono">{order.tracking_number}</div>
                          <div className="text-muted-foreground">{order.courier_name}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">Not added</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-medium">₹{order.order_amount}</td>
                    <td className="p-3 text-right text-red-600">₹{order.shipping_cost}</td>
                    <td className="p-3 text-right text-red-600">₹{order.cod_charge}</td>
                    <td className="p-3 text-right font-semibold text-green-700">
                      ₹{(order.order_amount || 0) - (order.total_logistics_cost || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {orders.length > 0 && (
                <tfoot>
                  <tr className="bg-muted font-semibold">
                    <td colSpan={8} className="p-3 text-right">TOTALS:</td>
                    <td className="p-3 text-right">₹{orders.reduce((sum, o) => sum + (o.order_amount || 0), 0).toLocaleString()}</td>
                    <td className="p-3 text-right text-red-600">₹{orders.reduce((sum, o) => sum + (o.shipping_cost || 0), 0).toLocaleString()}</td>
                    <td className="p-3 text-right text-red-600">₹{orders.reduce((sum, o) => sum + (o.cod_charge || 0), 0).toLocaleString()}</td>
                    <td className="p-3 text-right text-green-700">
                      ₹{orders.reduce((sum, o) => sum + ((o.order_amount || 0) - (o.total_logistics_cost || 0)), 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {orders.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No shipments found
            </div>
          )}
        </Card>

        {/* Shipping Rates Modal */}
        <Dialog open={showRatesModal} onOpenChange={setShowRatesModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Shipping Rates Configuration
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <p className="text-sm text-muted-foreground">
                Configure shipping rates for each zone. These are used to calculate logistics costs.
              </p>

              {/* Delhi */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-800">Delhi</Badge>
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Base (500g)</Label>
                    <Input 
                      type="number" 
                      value={rates.delhi.base_500g}
                      onChange={(e) => updateRate('delhi', 'base_500g', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Per Kg (additional)</Label>
                    <Input 
                      type="number" 
                      value={rates.delhi.per_kg}
                      onChange={(e) => updateRate('delhi', 'per_kg', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">COD Charge</Label>
                    <Input 
                      type="number" 
                      value={rates.delhi.cod_charge}
                      onChange={(e) => updateRate('delhi', 'cod_charge', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* NCR */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-800">NCR</Badge>
                  <span className="text-xs text-muted-foreground">(Gurgaon, Noida, Faridabad, Ghaziabad)</span>
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Base (500g)</Label>
                    <Input 
                      type="number" 
                      value={rates.ncr.base_500g}
                      onChange={(e) => updateRate('ncr', 'base_500g', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Per Kg (additional)</Label>
                    <Input 
                      type="number" 
                      value={rates.ncr.per_kg}
                      onChange={(e) => updateRate('ncr', 'per_kg', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">COD Charge</Label>
                    <Input 
                      type="number" 
                      value={rates.ncr.cod_charge}
                      onChange={(e) => updateRate('ncr', 'cod_charge', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* North */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">North Zone</Badge>
                  <span className="text-xs text-muted-foreground">(Punjab, Haryana, UP, Rajasthan, etc.)</span>
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Base (500g)</Label>
                    <Input 
                      type="number" 
                      value={rates.north.base_500g}
                      onChange={(e) => updateRate('north', 'base_500g', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Per Kg (additional)</Label>
                    <Input 
                      type="number" 
                      value={rates.north.per_kg}
                      onChange={(e) => updateRate('north', 'per_kg', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">COD Charge</Label>
                    <Input 
                      type="number" 
                      value={rates.north.cod_charge}
                      onChange={(e) => updateRate('north', 'cod_charge', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* ROI */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge className="bg-orange-100 text-orange-800">Rest of India</Badge>
                  <span className="text-xs text-muted-foreground">(All other states)</span>
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Base (500g)</Label>
                    <Input 
                      type="number" 
                      value={rates.roi.base_500g}
                      onChange={(e) => updateRate('roi', 'base_500g', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Per Kg (additional)</Label>
                    <Input 
                      type="number" 
                      value={rates.roi.per_kg}
                      onChange={(e) => updateRate('roi', 'per_kg', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">COD Charge</Label>
                    <Input 
                      type="number" 
                      value={rates.roi.cod_charge}
                      onChange={(e) => updateRate('roi', 'cod_charge', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRatesModal(false)}>
                  Cancel
                </Button>
                <Button onClick={saveRates} disabled={savingRates}>
                  {savingRates ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Rates'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Remittance Period Modal */}
        <Dialog open={showRemittanceModal} onOpenChange={setShowRemittanceModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Create COD Remittance Period
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Select the date range for delivered COD orders. The system will calculate the expected remittance from the courier.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period Start Date</Label>
                  <Input 
                    type="date"
                    value={newRemittance.period_start}
                    onChange={(e) => setNewRemittance({...newRemittance, period_start: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Period End Date</Label>
                  <Input 
                    type="date"
                    value={newRemittance.period_end}
                    onChange={(e) => setNewRemittance({...newRemittance, period_end: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Input 
                  placeholder="e.g., First fortnight of March"
                  value={newRemittance.notes}
                  onChange={(e) => setNewRemittance({...newRemittance, notes: e.target.value})}
                />
              </div>

              <Button 
                variant="outline" 
                onClick={calculateRemittance}
                disabled={!newRemittance.period_start || !newRemittance.period_end}
                className="w-full"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculate Expected Amount
              </Button>

              {calculatedRemittance && (
                <Card className="p-4 bg-green-50 border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">Calculation Result</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Delivered COD Orders:</span>
                      <span className="font-semibold ml-2">{calculatedRemittance.summary?.total_orders || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total COD Collected:</span>
                      <span className="font-semibold ml-2">₹{(calculatedRemittance.summary?.total_cod_collected || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-green-300">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-800">Courier Will Send You:</span>
                      <span className="text-2xl font-bold text-green-700">
                        ₹{(calculatedRemittance.summary?.expected_remittance || 0).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      Full COD amount (no deductions)
                    </p>
                  </div>

                  <div className="mt-3 pt-3 border-t border-green-300 bg-amber-50 -mx-4 -mb-4 p-4 rounded-b-lg">
                    <h5 className="font-medium text-amber-800 mb-2">You Pay Courier Later:</h5>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-amber-700">Shipping Cost:</span>
                        <span className="font-semibold ml-2">₹{(calculatedRemittance.summary?.total_shipping_cost || 0).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-amber-700">COD Charges:</span>
                        <span className="font-semibold ml-2">₹{(calculatedRemittance.summary?.total_cod_charges || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-amber-300">
                      <span className="font-semibold text-amber-800">Total to Pay Courier:</span>
                      <span className="text-xl font-bold text-amber-700">
                        ₹{(calculatedRemittance.summary?.courier_charges_to_pay || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-amber-300 bg-white/50 -mx-4 px-4 py-2 rounded-b-lg">
                      <span className="font-semibold text-green-800">Your Net Profit:</span>
                      <span className="text-xl font-bold text-green-700">
                        ₹{(calculatedRemittance.summary?.net_after_courier || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowRemittanceModal(false);
                  setCalculatedRemittance(null);
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={createRemittance} 
                  disabled={!calculatedRemittance || creatingRemittance}
                >
                  {creatingRemittance ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Period'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Record Payment Modal */}
        <Dialog open={showRecordPaymentModal} onOpenChange={setShowRecordPaymentModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                Record Courier Payment
              </DialogTitle>
            </DialogHeader>

            {selectedRemittance && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm space-y-1">
                    <p><strong>Period:</strong> {selectedRemittance.period_start} to {selectedRemittance.period_end}</p>
                    <p><strong>Orders:</strong> {selectedRemittance.order_count}</p>
                    <p><strong>Expected Amount:</strong> <span className="text-lg font-bold text-green-600">₹{selectedRemittance.expected_amount?.toLocaleString()}</span></p>
                  </div>
                </div>

                <div>
                  <Label>Amount Received from Courier (₹)</Label>
                  <Input 
                    type="number"
                    placeholder="Enter exact amount received"
                    value={receivedAmount}
                    onChange={(e) => setReceivedAmount(e.target.value)}
                  />
                </div>

                <div>
                  <Label>Payment Received Date</Label>
                  <Input 
                    type="date"
                    value={receivedDate}
                    onChange={(e) => setReceivedDate(e.target.value)}
                  />
                </div>

                {receivedAmount && (
                  <div className={`p-3 rounded-lg ${
                    parseFloat(receivedAmount) === selectedRemittance.expected_amount ? 'bg-green-50 text-green-700' :
                    parseFloat(receivedAmount) < selectedRemittance.expected_amount ? 'bg-red-50 text-red-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    <div className="flex items-center gap-2">
                      {parseFloat(receivedAmount) === selectedRemittance.expected_amount ? (
                        <><CheckCircle className="w-5 h-5" /> Exact amount!</>
                      ) : parseFloat(receivedAmount) < selectedRemittance.expected_amount ? (
                        <><AlertTriangle className="w-5 h-5" /> Short by ₹{(selectedRemittance.expected_amount - parseFloat(receivedAmount)).toLocaleString()}</>
                      ) : (
                        <><CheckCircle className="w-5 h-5" /> Excess of ₹{(parseFloat(receivedAmount) - selectedRemittance.expected_amount).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setShowRecordPaymentModal(false);
                    setSelectedRemittance(null);
                    setReceivedAmount('');
                    setReceivedDate('');
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={recordPayment}>
                    Record Payment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminLogisticsPage;
